import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

// How long the button stays in the "Copied" state before reverting to "Copy".
const COPIED_FEEDBACK_DURATION_MS = 2200;

// Reusable "Copy link" pill — used inside FCC and Donate sections so the
// Aradhana team can quickly grab shareable links to send via WhatsApp/email.
//
// Props:
//   url       full URL to copy
//   label     button text (default: "Copy share link")
//   variant   "light" (default — for dark backgrounds) | "dark" (for cream backgrounds)
export default function CopyLinkButton({
  url,
  label = "Copy share link",
  variant = "light",
  testId = "copy-link-button",
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older Safari / WebViews
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Link copied — ready to share");
      setTimeout(() => setCopied(false), COPIED_FEEDBACK_DURATION_MS);
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  const light =
    "border border-[#fdfbf7]/30 text-[#fdfbf7] hover:bg-[#fdfbf7]/10";
  const dark =
    "border border-[#1a1a1a]/25 text-[#1a1a1a] hover:bg-[#1a1a1a]/5";

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid={testId}
      aria-label={`Copy link: ${url}`}
      className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
        variant === "dark" ? dark : light
      }`}
    >
      {copied ? (
        <Check size={14} strokeWidth={1.6} />
      ) : (
        <Copy size={14} strokeWidth={1.6} />
      )}
      {copied ? "Copied" : label}
    </button>
  );
}
